import { Component, OnInit, Renderer2 } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { forkJoin, map, of } from 'rxjs';
import { PATHS } from 'src-ironside/app-routing.module';
import { Favorite, FavoriteRepository } from 'src-ironside/repositories/favorite.repository';
import { Product, ProductRepository } from 'src-ironside/repositories/product.repository';
import { Resource, ResourceRepository } from 'src-ironside/repositories/resource.repository';
import { AccueilDataService } from 'src-ironside/services/accueil-data.service';
import { ScreenMediator } from 'src-ironside/services/screen.mediator';
import { SessionStorageService } from 'src-ironside/services/session-storage.service';
import { BreadCrumbItem } from 'src-ironside/shared/components/bread-crumb/bread-crumb.component';
import { ModifyFavoriteComponent } from './modify-favorite/modify-favorite';

@Component({
    selector: 'app-my-favourites',
    templateUrl: 'my-favourites.component.html',
    styleUrls: ['./my-favourites.component.css'],
    providers: [ProductRepository, FavoriteRepository, ConfirmationService, MessageService],
})
export class MyFavouritesComponent implements OnInit {
    favList: any;
    loading: boolean = false;
    constructor(
        public favoriteRepository: FavoriteRepository,
        private dialog: MatDialog,
        private screenMediator: ScreenMediator,
        private accueilDataService: AccueilDataService,
        private translate: TranslateService,
        private sessionStorageService: SessionStorageService,
        private resourceRepository: ResourceRepository,
        private productRepository: ProductRepository,
        private confirmationService: ConfirmationService,
        private messageService: MessageService,
        private renderer: Renderer2
    ) { }

    ngOnInit(): void {
        this.fetchMyFavorites();
    }

    fetchMyFavorites() {
        this.loading = true;
        this.favoriteRepository.getMyFavoriteList().subscribe({
            next: (favs) => {
                //this.favoriteRepository.updateFavorites(favs);
                const productIds: string[] = [];
                favs.map((el: any) => {
                    el?.obj.linkToProduct.forEach((el: any) => productIds.push(el.documentId));
                });

                if (productIds.length === 0) {
                    this.favList = favs;
                    this.loading = false;
                    return;
                }

                this.productRepository.findByIds(productIds).subscribe({
                    next: (res) => {
                        this.productRepository
                            .loadFullExtendDataByGroup(res.content, false)
                            .subscribe({
                                next: (res) => {
                                    favs.map((el: any) => {
                                        let resultImg: any = [];
                                        el.obj?.linkToProduct.forEach((item: any) => {
                                            const foundProduct = res?.find(
                                                (i: any) => i.id == item?.documentId
                                            );
                                            const result = this.getResourceValid(
                                                foundProduct?.fullExtendData
                                            );
                                            if (result) {
                                                resultImg.push(result);
                                            }
                                        });

                                        el.img = resultImg.filter((i: any) => i != undefined);
                                    });

                                    this.favList = favs;

                                    this.loading = false;
                                },
                                error: (error) => {
                                    console.error('Error loading product data:', error);
                                    this.loading = false;
                                },
                            });
                    },
                    error: (error) => {
                        console.error('Error loading product data:', error);
                        this.loading = false;
                    },
                    complete: () => {
                        this.loading = false;
                    },
                });
            },
            error: (error) => {
                console.error('Error fetching favorites:', error);
                this.loading = false;
            },
        });
    }

    getResourceValid(value: any) {
        if (Array.isArray(value) && value.length) {
            const result = value.flatMap((el: any) => {
                return (
                    el?.resources?.filter(
                        (item: any) => item?.files && item?.typeOfResource === 'PHOTO'
                    ) || []
                );
            });
            return result.length
                ? result?.[0]?.files?.[0]?.url
                : 'src-ironside/assets/images/image-default.jpg';
        } else {
            return 'src-ironside/assets/images/image-default.jpg';
        }
    }

    fetchImageProduct(products: Product[]) {
        const productGroups = [];

        for (let i = 0; i < products.length; i += 10) {
            productGroups.push(products.slice(i, i + 10));
        }

        const observables = productGroups.map((group) => {
            const resourceIds = group.flatMap((product) => product.resources || []);
            return resourceIds.length
                ? this.resourceRepository.getListImageResource(resourceIds)
                : of([]);
        });

        return forkJoin(observables).pipe(
            map((results) => {
                const combinedImages = results.flatMap((item) => item);

                products.forEach((product) => {
                    const matchingImage: Resource | undefined = combinedImages.find(
                        (image) => image?.id === product?.resources?.[0]
                    );
                    if (matchingImage) {
                        product.image = matchingImage?.files?.[0]?.url || '';
                    }
                });
            })
        );
    }

    get breadCrumb() {
        let breadCrumbs: BreadCrumbItem[] = [
            { name: this.translate.instant('Menu.home'), url: PATHS.home_en },
            { name: this.translate.instant('Menu.my-favorites') },
        ];
        return breadCrumbs;
    }

    deleteFavorite(id: string, event: MouseEvent) {
        event.stopPropagation();

        this.confirmationService.confirm({
            target: event.target as EventTarget,
            message: this.translate.instant('IRONSIDE.my-favourites.mes-delete-fav'),
            icon: 'pi pi-info-circle',
            acceptLabel: this.translate.instant('IRONSIDE.my-account.DELETE'),
            rejectLabel: this.translate.instant('IRONSIDE.product-catalog-dialog.CANCEL'),
            acceptButtonStyleClass: 'p-button-warning p-button-sm',
            rejectButtonStyleClass: 'p-button-secondary p-button-sm',
            accept: () => {
                this.favoriteRepository.delete(id).subscribe({
                    next: () => {
                        this.messageService.add({
                            severity: 'success',
                            detail: this.translate.instant(
                                'IRONSIDE.my-favourites.mes-delete-fav-success'
                            ),
                        });
                        this.fetchMyFavorites();
                    },
                    error: () => {
                        this.messageService.add({
                            severity: 'error',
                            detail: this.translate.instant(
                                'IRONSIDE.my-favourites.mes-delete-fav-error'
                            ),
                        });
                    },
                });
            },
            reject: () => { },
        });
    }

    createFavorite() {
        const dialogRef = this.dialog.open(ModifyFavoriteComponent);
        dialogRef.afterClosed().subscribe((res) => {
            if (res) {
                this.fetchMyFavorites();
            }
        });
    }

    editFavorite(favorite: Favorite, event: MouseEvent) {
        event.stopPropagation();
        this.dialog.open(ModifyFavoriteComponent, {
            data: favorite,
        });
    }

    toSearchPage(item: any) {
        this.accueilDataService.filterData = { favListId: item.id, favListName: item.name };
        const ids: string[] = item?.obj?.linkToProduct?.map((el: any) => el.documentId) || [];
        const productIdsFav = this.favoriteRepository.getProductsByFavId(item?.id);
        this.sessionStorageService.setFilters({
            isfavLis: true,
            favListName: item.name,
            favListId: item.id,
            productIdsFav: ids,
        });
        this.screenMediator.toRecherche();
    }

    get shouldShowSpinner(): boolean {
        try {
            const element = this.renderer.selectRootElement('#outter-loading', true);
            return !!element;
        } catch (e) {
            return false;
        }
    }
}
